import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Konva from 'konva';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import NearMeIcon from '@mui/icons-material/NearMe';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import CallMadeIcon from '@mui/icons-material/CallMade';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import RectangleIcon from '@mui/icons-material/Rectangle';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import CropIcon from '@mui/icons-material/Crop';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import SaveIcon from '@mui/icons-material/Save';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import type { FileApi } from '../../../services/sandboxService';
import { FileEntity } from '../../../types';
import ConfirmDialog from '../ConfirmDialog';
import WordImageSizeToggle from '../WordImageSizeToggle';
import { parseWordImageSize, WORD_IMAGE_SIZE_DEFAULT, type WordImageSize } from '../wordPageLayout';
import AnnotationStage from './AnnotationStage';
import { exportStageToPngBlob, loadImageFromBlob } from './exportImage';
import { computeFitScale, stabilizeViewportSize } from './fitZoom';
import { useAnnotationHistory } from './useAnnotationHistory';
import {
  ANNOTATION_COLORS,
  AnnotationDocument,
  AnnotationShape,
  CropRect,
  SIZE_PRESETS,
  SizePreset,
  ToolId,
  emptyAnnotationDocument,
  parseAnnotationDocument,
} from './types';

export interface ImageAnnotationEditorProps {
  open: boolean;
  onClose: () => void;
  /** Zu bearbeitende Datei: Original oder bereits bearbeitete Version. */
  file: FileEntity | null;
  /** Alle Dateien der Schwachstelle – zum Auffinden des Originals einer bearbeiteten Version. */
  files: FileEntity[];
  fileApi: FileApi;
  /** "Speichern & einfügen" anbieten (ein Texteditor ist aktiv). */
  canInsert?: boolean;
  onSaved: (saved: FileEntity, options: { insert: boolean; createdNew: boolean; wordSize: WordImageSize }) => void;
  /** Ausgangs-Preset beim Öffnen (Default: Halb). */
  initialWordSize?: WordImageSize;
}

interface ToolDef {
  id: ToolId;
  label: string;
  shortcut: string;
  icon: React.ReactElement;
  hint: string;
}

const TOOLS: ToolDef[] = [
  { id: 'select', label: 'Auswählen / Verschieben', shortcut: 'V', icon: <NearMeIcon fontSize="small" />, hint: 'Element anklicken, verschieben oder an den Griffen skalieren. Entf löscht die Auswahl.' },
  { id: 'rect', label: 'Rahmen', shortcut: 'R', icon: <CropSquareIcon fontSize="small" />, hint: 'Bereich aufziehen, um ihn einzurahmen.' },
  { id: 'ellipse', label: 'Ellipse', shortcut: 'E', icon: <CircleOutlinedIcon fontSize="small" />, hint: 'Bereich aufziehen, um ihn oval einzukreisen.' },
  { id: 'arrow', label: 'Pfeil', shortcut: 'A', icon: <CallMadeIcon fontSize="small" />, hint: 'Vom Startpunkt zum Ziel ziehen – die Spitze zeigt auf das Ziel.' },
  { id: 'highlight', label: 'Textmarker', shortcut: 'H', icon: <BorderColorIcon fontSize="small" />, hint: 'Bereich aufziehen, um ihn halbtransparent hervorzuheben.' },
  { id: 'text', label: 'Text', shortcut: 'T', icon: <TextFieldsIcon fontSize="small" />, hint: 'Klicken, um ein Textlabel zu platzieren. Text oben im Feld ändern.' },
  { id: 'marker', label: 'Nummerierter Marker', shortcut: 'N', icon: <LooksOneIcon fontSize="small" />, hint: 'Klicken, um einen fortlaufend nummerierten Marker zu setzen.' },
  { id: 'pixelate', label: 'Verpixeln', shortcut: 'P', icon: <BlurOnIcon fontSize="small" />, hint: 'Bereich aufziehen, um ihn zu verpixeln (z. B. Namen, Tokens).' },
  { id: 'redact', label: 'Schwärzen', shortcut: 'B', icon: <RectangleIcon fontSize="small" />, hint: 'Bereich aufziehen, um ihn deckend zu übermalen (z. B. Passwörter).' },
  { id: 'spotlight', label: 'Spotlight / Fokus', shortcut: 'S', icon: <CenterFocusStrongIcon fontSize="small" />, hint: 'Bereich aufziehen – alles außerhalb wird abgedunkelt.' },
  { id: 'crop', label: 'Zuschneiden', shortcut: 'C', icon: <CropIcon fontSize="small" />, hint: 'Zuschnitt-Rahmen anpassen und oben übernehmen. Der Zuschnitt bleibt jederzeit änderbar.' },
];

const SHORTCUTS: Record<string, ToolId> = Object.fromEntries(TOOLS.map((t) => [t.shortcut.toLowerCase(), t.id]));

const ZOOM_STEPS = [0.25, 0.33, 0.5, 0.67, 0.75, 1, 1.25, 1.5, 2, 3, 4];

const isTypingTarget = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
};

const ImageAnnotationEditor: React.FC<ImageAnnotationEditorProps> = ({
  open,
  onClose,
  file,
  files,
  fileApi,
  canInsert = false,
  onSaved,
  initialWordSize,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  /* Basisbild + Modus bestimmen: bearbeitete Version → Original laden, sonst Datei selbst. */
  const original = useMemo(
    () => (file?.original_file_id ? files.find((f) => f.id === file.original_file_id) ?? null : null),
    [file, files],
  );
  const mode: 'create' | 'update' = file && original ? 'update' : 'create';
  const baseFile = mode === 'update' ? original : file;

  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { doc, commit, undo, redo, reset, canUndo, canRedo } = useAnnotationHistory(emptyAnnotationDocument());

  const [tool, setTool] = useState<ToolId>('select');
  const [color, setColor] = useState<string>(ANNOTATION_COLORS[0].value);
  const [size, setSize] = useState<SizePreset>('M');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [zoom, setZoom] = useState<'fit' | number>('fit');
  const [wordSize, setWordSize] = useState<WordImageSize>(WORD_IMAGE_SIZE_DEFAULT);
  const [cropMode, setCropMode] = useState(false);
  const [draftCrop, setDraftCrop] = useState<CropRect>({ x: 0, y: 0, width: 1, height: 1 });
  const [discardOpen, setDiscardOpen] = useState(false);

  const stageRef = useRef<Konva.Stage | null>(null);
  const uiLayerRef = useRef<Konva.Layer | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const textFieldRef = useRef<HTMLInputElement | null>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  /* ── Laden ─────────────────────────────────────────────────── */
  useEffect(() => {
    if (!open || !file || !baseFile) return;
    let cancelled = false;
    setImage(null);
    setLoadError(null);
    setSaveError(null);
    setSelectedId(null);
    setTool('select');
    setZoom('fit');
    setWordSize(parseWordImageSize(initialWordSize));
    setCropMode(false);
    reset(mode === 'update' ? parseAnnotationDocument(file.annotationen) : emptyAnnotationDocument());

    (async () => {
      try {
        // getPreviewUrl liefert bereits eine vollstaendige URL (inkl. API_BASE). Sie darf nicht
        // durch die Axios-Instanz laufen, sonst wird baseURL erneut vorangestellt (/api/api/...).
        const response = await fetch(fileApi.getPreviewUrl(baseFile.id), { credentials: 'include' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const img = await loadImageFromBlob(await response.blob());
        if (!cancelled) setImage(img);
      } catch (e) {
        console.error('Bild konnte nicht geladen werden:', e);
        if (!cancelled) setLoadError('Das Bild konnte nicht geladen werden.');
      }
    })();
    return () => {
      cancelled = true;
    };
    // Absichtlich nur bei Oeffnen/Dateiwechsel laden (nicht bei jeder Listen-Aenderung).
  }, [open, file?.id]);

  /* Viewport messen — nicht die Stage. Sonst: Stage-Groesse → Scrollbar/1px → Fit neu → Zittern. */
  useEffect(() => {
    if (!open) return;
    const el = viewportRef.current;
    if (!el) return;
    const apply = (width: number, height: number) => {
      setContainerSize((prev) => stabilizeViewportSize(prev, { width, height }));
    };
    const read = () => apply(el.clientWidth, el.clientHeight);
    read();
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        read();
        return;
      }
      const box = entry.contentBoxSize?.[0];
      if (box) {
        apply(box.inlineSize, box.blockSize);
        return;
      }
      apply(entry.contentRect.width, entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, image]);

  const imageBounds = useMemo(
    () => (image ? { width: image.naturalWidth || image.width, height: image.naturalHeight || image.height } : null),
    [image],
  );

  const visible = useMemo<CropRect | null>(() => {
    if (!imageBounds) return null;
    if (cropMode || !doc.crop) return { x: 0, y: 0, ...imageBounds };
    return doc.crop;
  }, [imageBounds, cropMode, doc.crop]);

  const fitScale = useMemo(() => {
    if (!visible) return 1;
    return computeFitScale(containerSize.width, containerSize.height, visible.width, visible.height);
  }, [visible, containerSize]);

  const scale = zoom === 'fit' ? fitScale : zoom;

  /* ── Aenderungen an Formen ─────────────────────────────────── */
  const selectedShape = useMemo(() => doc.shapes.find((s) => s.id === selectedId) ?? null, [doc.shapes, selectedId]);

  const updateSelected = useCallback(
    (patch: Partial<AnnotationShape> & Record<string, unknown>) => {
      if (!selectedId) return;
      commit((prev) => ({
        ...prev,
        shapes: prev.shapes.map((s) => (s.id === selectedId ? ({ ...s, ...patch } as AnnotationShape) : s)),
      }));
    },
    [commit, selectedId],
  );

  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    commit((prev) => ({ ...prev, shapes: prev.shapes.filter((s) => s.id !== selectedId) }));
    setSelectedId(null);
  }, [commit, selectedId]);

  const handleColorChange = (value: string) => {
    setColor(value);
    if (selectedShape) updateSelected({ color: value });
  };

  const handleSizeChange = (value: SizePreset) => {
    setSize(value);
    if (!selectedShape) return;
    const p = SIZE_PRESETS[value];
    const patch: Record<string, unknown> = { strokeWidth: p.strokeWidth };
    if (selectedShape.type === 'text') patch.fontSize = p.fontSize;
    if (selectedShape.type === 'marker') patch.radius = p.radius;
    updateSelected(patch);
  };

  const selectTool = useCallback(
    (next: ToolId) => {
      if (!imageBounds) return;
      if (next === 'crop') {
        setSelectedId(null);
        setDraftCrop(doc.crop ?? { x: 0, y: 0, ...imageBounds });
        setCropMode(true);
        setTool('crop');
        return;
      }
      setCropMode(false);
      setTool(next);
      if (next === 'redact') setColor('#000000');
    },
    [doc.crop, imageBounds],
  );

  const applyCrop = () => {
    if (!imageBounds) return;
    const isFull =
      draftCrop.x === 0 && draftCrop.y === 0 && draftCrop.width === imageBounds.width && draftCrop.height === imageBounds.height;
    commit((prev) => ({ ...prev, crop: isFull ? null : draftCrop }));
    setCropMode(false);
    setTool('select');
  };

  const removeCrop = () => {
    commit((prev) => ({ ...prev, crop: null }));
    setCropMode(false);
    setTool('select');
  };

  const cancelCrop = () => {
    setCropMode(false);
    setTool('select');
  };

  const focusTextField = useCallback(() => {
    window.setTimeout(() => {
      textFieldRef.current?.focus();
      textFieldRef.current?.select();
    }, 30);
  }, []);

  const handleTextEdit = useCallback(
    (id: string) => {
      setSelectedId(id);
      focusTextField();
    },
    [focusTextField],
  );

  /* ── Zoom ──────────────────────────────────────────────────── */
  const zoomBy = (direction: 1 | -1) => {
    const current = scale;
    const next =
      direction === 1
        ? ZOOM_STEPS.find((z) => z > current + 0.001)
        : [...ZOOM_STEPS].reverse().find((z) => z < current - 0.001);
    if (next) setZoom(next);
  };

  const requestClose = useCallback(() => {
    if (canUndo) setDiscardOpen(true);
    else onClose();
  }, [canUndo, onClose]);

  /* ── Tastatur ──────────────────────────────────────────────── */
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if (mod && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redo();
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedId) {
          e.preventDefault();
          deleteSelected();
        }
        return;
      }
      if (e.key === 'Escape') {
        if (discardOpen) return;
        // Capture-Phase + stopPropagation: darunterliegende Escape-Handler (z. B. Fokusmodus des Texteditors) nicht ausloesen
        e.preventDefault();
        e.stopPropagation();
        if (cropMode) cancelCrop();
        else if (selectedId) setSelectedId(null);
        else if (tool !== 'select') setTool('select');
        else requestClose();
        return;
      }
      if (!mod && !e.altKey) {
        const t = SHORTCUTS[e.key.toLowerCase()];
        if (t) {
          e.preventDefault();
          selectTool(t);
        }
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [open, undo, redo, selectedId, deleteSelected, cropMode, tool, selectTool, discardOpen, requestClose]);

  /* ── Speichern / Schliessen ────────────────────────────────── */
  const handleSave = async (insert: boolean) => {
    const stage = stageRef.current;
    if (!stage || !file || !baseFile || cropMode) return;
    setSaving(true);
    setSaveError(null);
    try {
      const blob = await exportStageToPngBlob(stage, uiLayerRef.current, scale);
      const payload: AnnotationDocument = doc;
      const json = JSON.stringify(payload);
      let saved: FileEntity;
      if (mode === 'update') {
        if (!fileApi.updateBearbeitung) throw new Error('Bearbeiten wird hier nicht unterstützt');
        saved = await fileApi.updateBearbeitung(file.id, blob, json);
      } else {
        if (!fileApi.createBearbeitung) throw new Error('Bearbeiten wird hier nicht unterstützt');
        saved = await fileApi.createBearbeitung(baseFile.id, blob, json);
      }
      onSaved(saved, { insert, createdNew: mode === 'create', wordSize });
    } catch (e) {
      console.error('Bild-Bearbeitung konnte nicht gespeichert werden:', e);
      setSaveError('Speichern fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setSaving(false);
    }
  };

  const currentTool = TOOLS.find((t) => t.id === tool) ?? TOOLS[0];
  const showSizeOption = !cropMode && (!selectedShape || ['rect', 'ellipse', 'arrow', 'text', 'marker'].includes(selectedShape.type));
  const showColorOption = !cropMode && (!selectedShape || !['pixelate', 'spotlight'].includes(selectedShape.type));

  const surface = isDark ? '#141829' : '#f4f6fa';
  const barBg = isDark ? '#1b2036' : '#ffffff';
  const border = isDark ? alpha('#fff', 0.08) : alpha('#000', 0.1);
  const checker = isDark ? '#1a1f33' : '#e6e9ef';
  const checkerAlt = isDark ? '#111528' : '#f7f8fb';

  return (
    <Dialog open={open} onClose={requestClose} fullScreen disableEscapeKeyDown PaperProps={{ sx: { bgcolor: surface } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        {/* Kopfzeile */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1,
            bgcolor: barBg,
            borderBottom: `1px solid ${border}`,
            flexShrink: 0,
          }}
        >
          <InsertPhotoIcon sx={{ color: theme.palette.primary.main }} />
          <Box sx={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
              Bild bearbeiten
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap title={file?.dateiname}>
              {file?.dateiname}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              color={mode === 'update' ? 'primary' : 'default'}
              label={
                mode === 'update'
                  ? `Bearbeitete Version von „${original?.dateiname}“ – Speichern überschreibt sie`
                  : 'Original bleibt erhalten – Speichern erzeugt eine bearbeitete Kopie'
              }
              sx={{ fontSize: '0.72rem', maxWidth: 520 }}
            />
          </Box>
          <Button onClick={requestClose} startIcon={<CloseIcon />} color="inherit" disabled={saving} data-testid="image-editor-cancel">
            Abbrechen
          </Button>
          <Tooltip title={cropMode ? 'Zuschnitt zuerst übernehmen oder abbrechen' : ''} arrow>
            <span>
              <Button
                variant={canInsert ? 'outlined' : 'contained'}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={() => handleSave(false)}
                disabled={!image || saving || cropMode}
                data-testid="image-editor-save"
              >
                Speichern
              </Button>
            </span>
          </Tooltip>
          {canInsert && (
            <Tooltip title={cropMode ? 'Zuschnitt zuerst übernehmen oder abbrechen' : 'Speichern und in den aktiven Texteditor einfügen'} arrow>
              <span>
                <Button
                  variant="contained"
                  startIcon={<InsertPhotoIcon />}
                  onClick={() => handleSave(true)}
                  disabled={!image || saving || cropMode}
                  data-testid="image-editor-save-insert"
                >
                  Speichern &amp; einfügen
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>

        {/* Optionsleiste */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.75,
            bgcolor: barBg,
            borderBottom: `1px solid ${border}`,
            flexWrap: 'wrap',
            minHeight: 48,
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            Bildgröße
          </Typography>
          <WordImageSizeToggle value={wordSize} onChange={setWordSize} testId="image-editor-word-size" />
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          {cropMode ? (
            <>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Zuschneiden
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {draftCrop.width} × {draftCrop.height} px
              </Typography>
              <Button size="small" variant="contained" startIcon={<CheckIcon />} onClick={applyCrop} data-testid="crop-apply">
                Zuschnitt übernehmen
              </Button>
              {doc.crop && (
                <Button size="small" variant="outlined" onClick={removeCrop}>
                  Zuschnitt entfernen
                </Button>
              )}
              <Button size="small" color="inherit" onClick={cancelCrop}>
                Abbrechen
              </Button>
            </>
          ) : (
            <>
              {showColorOption && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5, fontWeight: 600 }}>
                    Farbe
                  </Typography>
                  {ANNOTATION_COLORS.map((c) => {
                    const active = c.value.toLowerCase() === color.toLowerCase();
                    return (
                      <Tooltip key={c.value} title={c.label} arrow>
                        <Box
                          component="button"
                          type="button"
                          data-testid={`color-${c.label}`}
                          onClick={() => handleColorChange(c.value)}
                          sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            bgcolor: c.value,
                            border: `2px solid ${active ? theme.palette.primary.main : isDark ? alpha('#fff', 0.35) : alpha('#000', 0.25)}`,
                            boxShadow: active ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                            cursor: 'pointer',
                            p: 0,
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
              )}
              {showSizeOption && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    Größe
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={size}
                    onChange={(_, v) => v && handleSizeChange(v as SizePreset)}
                    sx={{ '& .MuiToggleButton-root': { px: 1.25, py: 0.25, fontSize: '0.75rem', fontWeight: 700 } }}
                  >
                    <ToggleButton value="S">S</ToggleButton>
                    <ToggleButton value="M">M</ToggleButton>
                    <ToggleButton value="L">L</ToggleButton>
                  </ToggleButtonGroup>
                </>
              )}

              {selectedShape && selectedShape.type === 'text' && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  <TextField
                    inputRef={textFieldRef}
                    size="small"
                    label="Text"
                    value={selectedShape.text}
                    onChange={(e) => updateSelected({ text: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === 'Escape') (e.target as HTMLInputElement).blur();
                    }}
                    sx={{ width: 260 }}
                    inputProps={{ 'data-testid': 'text-shape-input' }}
                  />
                </>
              )}
              {selectedShape && selectedShape.type === 'marker' && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  <TextField
                    size="small"
                    type="number"
                    label="Nummer"
                    value={selectedShape.number}
                    onChange={(e) => updateSelected({ number: Math.max(0, parseInt(e.target.value || '0', 10)) })}
                    sx={{ width: 100 }}
                  />
                </>
              )}
              {selectedShape && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={deleteSelected} data-testid="shape-delete">
                    Löschen
                  </Button>
                </>
              )}

              <Box sx={{ flex: 1 }} />

              <Tooltip title="Rückgängig (Strg+Z)" arrow>
                <span>
                  <IconButton size="small" onClick={undo} disabled={!canUndo}>
                    <UndoIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Wiederholen (Strg+Y)" arrow>
                <span>
                  <IconButton size="small" onClick={redo} disabled={!canRedo}>
                    <RedoIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <Tooltip title="Verkleinern" arrow>
                <IconButton size="small" onClick={() => zoomBy(-1)}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography
                variant="caption"
                data-testid="image-editor-zoom"
                sx={{ minWidth: 40, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
              >
                {Math.round(scale * 100)} %
              </Typography>
              <Tooltip title="Vergrößern" arrow>
                <IconButton size="small" onClick={() => zoomBy(1)}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Einpassen" arrow>
                <IconButton size="small" onClick={() => setZoom('fit')} color={zoom === 'fit' ? 'primary' : 'default'}>
                  <FitScreenIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button size="small" color="inherit" onClick={() => setZoom(1)} sx={{ minWidth: 0, px: 1, fontSize: '0.72rem' }}>
                100 %
              </Button>
            </>
          )}
        </Box>

        {/* Werkzeuge + Zeichenflaeche */}
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.25,
              p: 0.75,
              bgcolor: barBg,
              borderRight: `1px solid ${border}`,
              overflowY: 'auto',
              flexShrink: 0,
            }}
          >
            {TOOLS.map((t, idx) => (
              <React.Fragment key={t.id}>
                {(idx === 1 || idx === 7 || idx === 10) && <Divider flexItem sx={{ my: 0.5 }} />}
                <Tooltip title={`${t.label} (${t.shortcut})`} placement="right" arrow>
                  <IconButton
                    size="small"
                    data-testid={`tool-${t.id}`}
                    onClick={() => selectTool(t.id)}
                    disabled={!image}
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: 1.5,
                      color: tool === t.id ? theme.palette.primary.main : theme.palette.text.secondary,
                      bgcolor: tool === t.id ? alpha(theme.palette.primary.main, 0.14) : 'transparent',
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                    }}
                  >
                    {t.icon}
                  </IconButton>
                </Tooltip>
              </React.Fragment>
            ))}
          </Box>

          <Box
            ref={viewportRef}
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                overflow: zoom === 'fit' ? 'hidden' : 'auto',
                display: 'flex',
                alignItems: zoom === 'fit' ? 'center' : 'flex-start',
                justifyContent: 'center',
                p: 2,
                backgroundColor: checker,
                backgroundImage: `linear-gradient(45deg, ${checkerAlt} 25%, transparent 25%), linear-gradient(-45deg, ${checkerAlt} 25%, transparent 25%), linear-gradient(45deg, transparent 75%, ${checkerAlt} 75%), linear-gradient(-45deg, transparent 75%, ${checkerAlt} 75%)`,
                backgroundSize: '24px 24px',
                backgroundPosition: '0 0, 0 12px, 12px -12px, -12px 0',
              }}
            >
              {loadError && <Alert severity="error" sx={{ alignSelf: 'center' }}>{loadError}</Alert>}
              {!image && !loadError && <CircularProgress sx={{ alignSelf: 'center' }} />}
              {image && (
                <Box
                  sx={{
                    boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
                    lineHeight: 0,
                    bgcolor: '#fff',
                    flexShrink: 0,
                  }}
                >
                  <AnnotationStage
                    image={image}
                    doc={doc}
                    onCommit={commit}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    tool={tool}
                    color={color}
                    size={size}
                    scale={scale}
                    cropMode={cropMode}
                    draftCrop={draftCrop}
                    onDraftCropChange={setDraftCrop}
                    stageRef={stageRef}
                    uiLayerRef={uiLayerRef}
                    onTextEdit={handleTextEdit}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Statuszeile */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2,
            py: 0.5,
            bgcolor: barBg,
            borderTop: `1px solid ${border}`,
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {currentTool.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }} noWrap>
            {currentTool.hint}
          </Typography>
          {saveError && (
            <Typography variant="caption" color="error">
              {saveError}
            </Typography>
          )}
          {imageBounds && (
            <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {doc.crop ? `${doc.crop.width} × ${doc.crop.height} px (zugeschnitten aus ${imageBounds.width} × ${imageBounds.height})` : `${imageBounds.width} × ${imageBounds.height} px`}
              {' · '}
              {doc.shapes.length} {doc.shapes.length === 1 ? 'Element' : 'Elemente'}
            </Typography>
          )}
        </Box>
      </Box>

      <ConfirmDialog
        open={discardOpen}
        title="Änderungen verwerfen?"
        message="Die Bearbeitung wurde noch nicht gespeichert. Möchten Sie die Änderungen verwerfen?"
        confirmText="Verwerfen"
        cancelText="Weiter bearbeiten"
        onConfirm={() => {
          setDiscardOpen(false);
          onClose();
        }}
        onCancel={() => setDiscardOpen(false)}
      />
    </Dialog>
  );
};

export default ImageAnnotationEditor;
